import { BulkCancelVisit } from '@/components/visits/dialogs/BulkCancelVisits/BulkCancelVisitsDialog';
import { useReassignVisitPermissionsFilter } from '@/components/visits/dialogs/ReassignVisits/reassignVisitPermissions';
import { EntrySource } from '@/components/visits/dialogs/ReassignVisits/v3/tracking/entrySource';
import { useCallback, useEffect, useMemo, useState } from 'react';

// A row reduced to what bulk actions need, so the rules don't depend on any one
// table's row shape or table library.
//
// `isCancellable` comes from the caller because surfaces disagree today. The
// dashboard excludes IN_REVIEW as well as ENDED, the others only ENDED, so picking
// one here would change cancel behavior somewhere.
export type BulkVisitTarget = {
    id: string;
    permissions: { assign: boolean; editState: boolean; editOutcome: boolean };
    hasServices: boolean;
    isCancellable: boolean;
    isEnded: boolean;
    patient: { id: string; fullName: string };
    city?: string | null;
};

type BulkVisitCapabilities = {
    reassign: boolean;
    cancel: boolean;
};

type UseBulkVisitActionsArgs<TRow> = {
    rows: TRow[];
    // Maps a row to a target, or null for rows bulk actions can't touch.
    toTarget: (row: TRow) => BulkVisitTarget | null;
    // Whether each action is offered here at all, from flags, config and permissions.
    capabilities: BulkVisitCapabilities;
    entrySource: EntrySource;
};

/**
 * Single source of the bulk-action rules every visits table needs.
 *
 * Buttons follow capability, so one is only hidden when the feature is off for this org,
 * and anything the rows can't satisfy disables it instead. The checkbox column also needs
 * a row some offered action applies to.
 *
 * Table bar decisions
 * `showReassign`, `showCancel`         which buttons show at all
 * `reassign`, `cancellableVisits`      what each button applies to, so it counts and
 *                                      disables itself
 *
 * Table row decisions
 * `showSelection`                      whether there are checkboxes at all
 * `isSelectableId`                     whether a given row's checkbox is enabled
 * `selectedIds`, `setSelectedIds`      the selection state the table reads and writes
 */
export const useBulkVisitActions = <TRow>({
    rows,
    toTarget,
    capabilities,
    entrySource,
}: UseBulkVisitActionsArgs<TRow>) => {
    const reassignVisitFilter = useReassignVisitPermissionsFilter();
    const [selectedIds, setSelectedIds] = useState<string[]>([]);

    // An ENDED visit has already happened, so there is nothing left to reassign. A
    // visit also needs a service, which the reassign flow's clinician and date
    // recommendations are computed from.
    const isReassignable = useCallback(
        (target: BulkVisitTarget) =>
            !target.isEnded &&
            (!reassignVisitFilter || reassignVisitFilter(target.permissions)) &&
            target.hasServices,
        [reassignVisitFilter],
    );

    const targets = useMemo(
        () => rows.map(toTarget).filter((target): target is BulkVisitTarget => target !== null),
        [rows, toTarget],
    );

    const reassignable = useMemo(
        () => (capabilities.reassign ? targets.filter(isReassignable) : []),
        [capabilities.reassign, targets, isReassignable],
    );
    const cancellable = useMemo(
        () => (capabilities.cancel ? targets.filter((target) => target.isCancellable) : []),
        [capabilities.cancel, targets],
    );

    const showReassign = capabilities.reassign;
    const showCancel = capabilities.cancel;

    const eligibleIds = useMemo(
        () => new Set([...reassignable, ...cancellable].map((target) => target.id)),
        [reassignable, cancellable],
    );

    // Keyed by id rather than by row, because the tables hand their predicate a row
    // of their own shape: converted rows on the unified tables, a TanStack wrapper on
    // the dashboard. Callers pass `(row) => isSelectableId(row.id)`.
    const isSelectableId = useCallback((id: string) => eligibleIds.has(id), [eligibleIds]);

    // Drop selections that are no longer eligible, such as rows leaving on a filter
    // change or a visit that stops qualifying after an action.
    useEffect(() => {
        setSelectedIds((prev) => {
            const filtered = prev.filter((id) => eligibleIds.has(id));
            return filtered.length === prev.length ? prev : filtered;
        });
    }, [eligibleIds]);

    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

    // Each action counts its own eligible subset, so the buttons can disagree.
    const reassign = useMemo(
        () => ({
            visitIds: reassignable
                .filter((target) => selectedSet.has(target.id))
                .map((target) => target.id),
            entrySource,
        }),
        [reassignable, selectedSet, entrySource],
    );

    const cancellableVisits = useMemo(
        (): BulkCancelVisit[] =>
            cancellable
                .filter((target) => selectedSet.has(target.id))
                .map((target) => ({
                    visitId: target.id,
                    patientId: target.patient.id,
                    patientName: target.patient.fullName,
                    city: target.city ?? undefined,
                })),
        [cancellable, selectedSet],
    );

    return {
        selectedIds,
        setSelectedIds,
        isSelectableId,
        showSelection: (showReassign || showCancel) && eligibleIds.size > 0,
        actionBarProps: {
            selectedCount: selectedIds.length,
            showReassign,
            showCancel,
            reassign,
            cancellableVisits,
        },
    };
};
